/**
 * Team-context error handling utilities.
 *
 * The Apollo errorLink is constructed inside `ApolloClientProvider`, which
 * sits above `TeamSelectionProvider` in the provider tree. The errorLink
 * still needs to react to the three team-context error codes by clearing
 * the active team and routing to Sentry, so the Provider registers a
 * recovery callback into this module-scoped registry on mount.
 *
 * Codes (from `backend-v2/src/common/utils/graphql-exceptions.ts`):
 * - `INVALID_ACTIVE_TEAM` — Sentry warning, clear active team, no toast.
 *   Next paint shows org-scoped data.
 * - `TEAM_REQUIRED` — no Sentry; UI surface area should render a "pick a
 *   team" prompt. None of the v1 resolvers throw this.
 * - `TEAM_FORBIDDEN_IN_HIDDEN` — Sentry exception. Indicates the FE gate
 *   leaked a teamId into a hidden-mode org; should never reach a user.
 * @module providers/teamSelectionErrorHandler
 */
import * as Sentry from "@sentry/react-native";
import type { GraphQLFormattedError } from "graphql";

import type { TeamSelectionMode } from "@/generated/graphql";

/**
 *
 */
export type TeamContextErrorCode =
  | "TEAM_FORBIDDEN_IN_HIDDEN"
  | "INVALID_ACTIVE_TEAM"
  | "TEAM_REQUIRED";

const TEAM_CONTEXT_ERROR_CODES: ReadonlySet<string> = new Set<string>([
  "TEAM_FORBIDDEN_IN_HIDDEN",
  "INVALID_ACTIVE_TEAM",
  "TEAM_REQUIRED",
]);

/**
 *
 */
interface Recovery {
  clearTeam: () => Promise<void>;
  getMode: () => TeamSelectionMode;
  /**
   * Active team id at the moment of the error. Used to skip the
   * `clearTeam` recovery when there is nothing to clear (e.g. the BE
   * threw `INVALID_ACTIVE_TEAM` from a ResolveField that read context
   * the FE didn't supply).
   */
  getActiveTeamId: () => string | null;
}

// eslint-disable-next-line functional/no-let -- module-scoped registry populated by TeamSelectionProvider on mount
let recovery: Recovery | null = null;

/**
 * Registers the recovery callback. Called by TeamSelectionProvider on mount
 * and on every value change.
 * @param next - Recovery functions
 * @returns Teardown function that clears the registry on unmount
 */
export function registerTeamSelectionRecovery(next: Recovery): () => void {
  recovery = next;
  return () => {
    if (recovery === next) {
      recovery = null;
    }
  };
}

/**
 * Inspects a GraphQL error array for team-context codes. The caller can use
 * the return value to decide whether to suppress further processing.
 *
 * Per SE-4934, POV is now carried in the `x-pov-team-id` request header
 * (set by `povTeamHeaderLink`) rather than as a per-operation `teamId`
 * variable. The previous "only clear if the failing op's variable matched
 * our active team" guard is therefore obsolete — every request that
 * carries our header is implicitly using our active team, so any
 * `INVALID_ACTIVE_TEAM` we receive while holding a non-null active team
 * means the header value we sent is no longer valid.
 * @param graphQLErrors - The graphQLErrors array from Apollo's onError link
 * @param operationName - The operation name for Sentry tagging
 * @returns True if any team-context code matched and was handled
 */
export function handleTeamSelectionErrors(
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
  operationName: string | undefined
): boolean {
  if (!graphQLErrors || graphQLErrors.length === 0) {
    return false;
  }

  // Deduplicate codes: a single response can contain hundreds of identical
  // errors (e.g. one per row of a connection where each ResolveField
  // resolves team-context independently). We only want to react once per
  // distinct code per response — otherwise `clearTeam` would fire in a
  // tight loop and `Sentry.captureMessage` would spam the inbox.
  const codes: ReadonlySet<string> = new Set(
    graphQLErrors
      .map(error => error.extensions?.code)
      .filter(
        (code): code is string =>
          typeof code === "string" && TEAM_CONTEXT_ERROR_CODES.has(code)
      )
  );

  if (codes.size === 0) {
    return false;
  }

  const mode = recovery?.getMode();
  const tags = {
    operationName: operationName ?? "unknown",
    mode: mode ?? "unknown",
  };

  if (codes.has("INVALID_ACTIVE_TEAM")) {
    Sentry.captureMessage("INVALID_ACTIVE_TEAM", {
      level: "warning",
      tags: { ...tags, code: "INVALID_ACTIVE_TEAM" },
    });
    // Clear when we currently hold a team. Since POV is now sent via the
    // `x-pov-team-id` header on every request, any INVALID_ACTIVE_TEAM
    // returned while we hold a team means the team we're sending is no
    // longer valid for this org.
    const activeTeamId = recovery?.getActiveTeamId() ?? null;
    if (recovery && activeTeamId !== null) {
      recovery.clearTeam().catch(clearError => {
        console.warn("clearTeam failed during error recovery:", clearError);
      });
    }
  }

  if (codes.has("TEAM_FORBIDDEN_IN_HIDDEN")) {
    Sentry.captureException(
      new Error("TEAM_FORBIDDEN_IN_HIDDEN: teamId sent for hidden-mode org"),
      { tags: { ...tags, code: "TEAM_FORBIDDEN_IN_HIDDEN" } }
    );
  }

  // TEAM_REQUIRED — no Sentry; consumers render a "pick a team" prompt
  // by reading mode + activeTeamId from useTeamSelection().
  return true;
}
